import { useCallback, useMemo } from 'react';
import { pick } from 'lodash';

import { ensureDefined } from '@e-socna/utils';
import { getFragmentData } from '@e-socna/data-layer';
import type {
  MissionStateId,
  MissionWorkflowId,
  FragmentType,
} from '@e-socna/data-layer';

import { useMissionAccess } from '../../components/Access/hooks/useMissionAccess.hook';
import { FolderBusinessEntityState } from '../../components/FolderBusinessEntityState';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { missionKindProfileOf } from '../../app/utils/mission-kind-profile.util';
import { MissionPage_GeneralInfo_Mission_Fragment } from '../../pages/Mission/GeneralInfo';

import type { RenderActionDialog } from '../FolderBusinessEntityState/types/action-dialog-render-props.type';

import { MissionArchiveInfoFields } from './ArchiveInfoFields';
import { useFindAllMissionActionPermissionsQuery } from './hooks/useFindAllMissionActionPermissionsQuery.hook';
import { useUpdateMissionStateSubmitFactory } from './hooks/useUpdateMissionStateSubmitFactory.hook';

interface Props {
  readonly enterEditionMode: () => void;
  readonly mission: FragmentType<
    typeof MissionPage_GeneralInfo_Mission_Fragment
  >;
  readonly isInEditionMode: boolean;
  readonly leaveEditionMode: () => void;
}

export const MissionStateForm = ({
  enterEditionMode,
  mission: pMission,
  isInEditionMode,
  leaveEditionMode,
}: Readonly<Props>) => {
  const mission = getFragmentData(
    MissionPage_GeneralInfo_Mission_Fragment,
    pMission,
  );

  const { missionStatesIdMap, missionWorkflowsIdMap } = useReferencesContext();

  const { missionState, missionWorkflow } = ensureDefined({
    missionWorkflow:
      missionWorkflowsIdMap[mission.workflowId as MissionWorkflowId],
    missionState: missionStatesIdMap[mission.stateId as MissionStateId],
  });

  const { currentNode } = ensureDefined(
    {
      currentNode: missionWorkflow.stateMachine.find(
        (node) => node.stateId === missionState._id,
      ),
    },
    'Current node not found',
  );

  const { data, isFetching } = useFindAllMissionActionPermissionsQuery(
    mission._id,
    isInEditionMode,
  );

  const useUpdateMissionStateSubmit = useUpdateMissionStateSubmitFactory(
    mission._id,
  );

  const currentNodeWithMissionState = useMemo(
    () => ({
      ...currentNode,
      ...pick(missionState, ['label', 'description', 'chip']),
    }),
    [currentNode, missionState],
  );

  const currentNodeActions = currentNode.actions;

  const renderActionDialog: RenderActionDialog = useCallback(
    (formikBag) => {
      const selectedAction = currentNodeActions.find(
        (action) => action._id === formikBag.values.actionId,
      );

      if (!selectedAction?.settings.needsCompleteArchiveInfo) {
        return null;
      }

      return {
        title: "Informations d'archive",
        // eslint-disable-next-line dot-notation -- index signature access required by noPropertyAccessFromIndexSignature
        isValid: !!formikBag.values['actionPayload'],
        content: <MissionArchiveInfoFields key={selectedAction._id} />,
      };
    },
    [currentNodeActions],
  );

  const { changeState } = useMissionAccess(mission._id, mission);

  return (
    <FolderBusinessEntityState
      actionPermissions={data?.findOneMission.access.actions}
      areActionPermissionsLoading={isFetching}
      currentNode={currentNodeWithMissionState}
      enableAction
      formTitle={`Mise à jour du Statut ${missionKindProfileOf(mission.kind).wording.ofName}`}
      gate={changeState}
      isInEditionMode={isInEditionMode}
      onCancel={leaveEditionMode}
      onEdit={enterEditionMode}
      renderActionDialog={renderActionDialog}
      useUpdateStateSubmit={useUpdateMissionStateSubmit}
    />
  );
};
